import { roundKg, toCentigrams } from '../units/round.js';

export interface RoundedLoad {
  /** Carga seleccionable elegida. `null` si no hay ninguna disponible. */
  loadKg: number | null;
  /** Desviacion relativa respecto a la carga cruda (0,04 = 4 %). */
  deviation: number;
  /** true si la desviacion supera el umbral y conviene ajustar repeticiones. */
  coarseIncrement: boolean;
  /** true si el redondeo se ha limitado para no rebasar un limite de seguridad. */
  boundedBySafety: boolean;
}

/** Por encima de esta desviacion se compensa con +/-1 repeticion. */
export const COARSE_INCREMENT_THRESHOLD = 0.04;

export interface RoundOptions {
  /** Limites duros que el redondeo NO puede rebasar. */
  minKg?: number;
  maxKg?: number;
}

/**
 * Redondea a la carga disponible mas proxima.
 * En empate exacto gana la MENOR (docs/LOAD_RECOMMENDATION_ENGINE.md §6.2).
 *
 * Si se pasan limites, el redondeo se hace DENTRO de ellos: redondear al valor
 * mas proximo podria rebasar por arriba un limite de seguridad ya aplicado
 * (p. ej. techo de +10 % entre sesiones), y ese limite manda sobre la cercania.
 */
export function roundToAvailableLoad(
  rawKg: number,
  availableKg: readonly number[],
  options: RoundOptions = {},
): RoundedLoad {
  if (availableKg.length === 0) {
    return { loadKg: null, deviation: 0, coarseIncrement: false, boundedBySafety: false };
  }

  const minCg = options.minKg === undefined ? null : toCentigrams(options.minKg);
  const maxCg = options.maxKg === undefined ? null : toCentigrams(options.maxKg);

  const withinBounds = availableKg.filter((candidate) => {
    const cg = toCentigrams(candidate);
    if (minCg !== null && cg < minCg) return false;
    if (maxCg !== null && cg > maxCg) return false;
    return true;
  });

  // Si ninguna carga cabe en el intervalo, se elige la mayor que no lo rebase
  // por arriba; y si tampoco existe, la menor disponible.
  let candidates = withinBounds;
  let boundedBySafety = withinBounds.length !== availableKg.length;
  if (candidates.length === 0) {
    const belowMax =
      maxCg === null ? [] : availableKg.filter((candidate) => toCentigrams(candidate) <= maxCg);
    candidates = belowMax.length > 0 ? [Math.max(...belowMax)] : [Math.min(...availableKg)];
    boundedBySafety = true;
  }

  const targetCg = toCentigrams(rawKg);
  let bestCg: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const candidate of candidates) {
    const candidateCg = toCentigrams(candidate);
    const distance = Math.abs(candidateCg - targetCg);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestCg = candidateCg;
    } else if (distance === bestDistance && bestCg !== null && candidateCg < bestCg) {
      // Empate exacto: se elige la carga menor.
      bestCg = candidateCg;
    }
  }

  if (bestCg === null) {
    return { loadKg: null, deviation: 0, coarseIncrement: false, boundedBySafety };
  }

  const chosen = roundKg(bestCg / 100);
  const deviation = targetCg === 0 ? 0 : Math.abs(bestCg - targetCg) / targetCg;
  return {
    loadKg: chosen,
    deviation,
    coarseIncrement: deviation > COARSE_INCREMENT_THRESHOLD,
    boundedBySafety,
  };
}

/**
 * Desglose de discos por lado para una carga de barra concreta.
 * Devuelve `null` si la carga no es alcanzable con el inventario.
 */
export function describePlateBreakdown(
  totalKg: number,
  barWeightKg: number,
  plateInventory: readonly { plateKg: number; pairs: number }[],
): number[] | null {
  const perSideCg = toCentigrams((totalKg - barWeightKg) / 2);
  if (perSideCg < 0) return null;
  if (perSideCg === 0) return [];

  const sorted = [...plateInventory].sort((a, b) => b.plateKg - a.plateKg);
  const used: number[] = [];
  let remaining = perSideCg;

  for (const item of sorted) {
    const plateCg = toCentigrams(item.plateKg);
    let count = 0;
    while (remaining >= plateCg && count < item.pairs) {
      remaining -= plateCg;
      count += 1;
      used.push(item.plateKg);
    }
  }

  return remaining === 0 ? used : null;
}
